import { useMemo } from 'react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import SearchSelect, { type SearchSelectOption } from '@/components/Shared/SearchSelect';
import { CategoryBadge } from '@/pages/Import/components/CategoryBadge';
import { useContractImportStore } from '@/store/useContractImportStore';
import { ContractStepFooter } from './ContractStepFooter';
import { Field, INPUT, Kicker, ModeToggle } from './wizardUi';

export function CustomerStep() {
  const { t } = useTranslation('contractImport');
  const { extraction, customer, customerMatch, contact, setCustomer, setContact, live } =
    useContractImportStore();

  const options: SearchSelectOption[] = useMemo(
    () => live.customers.map((c) => ({ id: c.id, label: c.name, description: c.customerNumber })),
    [live.customers],
  );

  const suggested: SearchSelectOption[] = customerMatch.suggestion
    ? [{
        id: customerMatch.suggestion.id,
        label: customerMatch.suggestion.name,
        description: customerMatch.suggestion.customerNumber,
      }]
    : [];

  const extracted = extraction?.customer;

  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-4">
          <p className="text-[13px] text-slate-600">{t('customer.hint')}</p>

          {/* Extraction summary */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1">
            <Kicker>{t('customer.extracted')}</Kicker>
            <div className="text-[13px] text-slate-900">{extracted?.name || '—'}</div>
            <div className="text-[12px] text-slate-500">
              {[extracted?.street, [extracted?.zip, extracted?.city].filter(Boolean).join(' '), extracted?.country]
                .filter(Boolean)
                .join(' · ') || '—'}
            </div>
            {extracted?.vatId && (
              <div className="text-[12px] text-slate-500 font-mono tabular-nums">{extracted.vatId}</div>
            )}
          </div>

          {/* Decision */}
          <div className="flex items-center gap-3">
            <ModeToggle mode={customer.mode} onChange={(mode) => setCustomer({ mode })} />
            <CategoryBadge category={customerMatch.category} score={customerMatch.score} />
          </div>

          {customer.mode === 'link' ? (
            <SearchSelect
              options={options}
              suggestedOptions={suggested}
              value={customer.existingCustomerId}
              onChange={(id) => setCustomer({ existingCustomerId: id })}
              placeholder={t('customer.pickCustomer')}
            />
          ) : (
            <div className="space-y-3">
              <Field label={t('customer.fields.name')}>
                <input className={INPUT} value={customer.name} onChange={(e) => setCustomer({ name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_0.6fr_1fr] gap-3">
                <Field label={t('customer.fields.street')}>
                  <input className={INPUT} value={customer.street} onChange={(e) => setCustomer({ street: e.target.value })} />
                </Field>
                <Field label={t('customer.fields.zip')}>
                  <input className={INPUT} value={customer.zip} onChange={(e) => setCustomer({ zip: e.target.value })} />
                </Field>
                <Field label={t('customer.fields.city')}>
                  <input className={INPUT} value={customer.city} onChange={(e) => setCustomer({ city: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('customer.fields.country')}>
                  <input className={INPUT} value={customer.country} onChange={(e) => setCustomer({ country: e.target.value })} />
                </Field>
                <Field label={t('customer.fields.vatId')}>
                  <input className={INPUT} value={customer.vatId} onChange={(e) => setCustomer({ vatId: e.target.value })} />
                </Field>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Contact */}
      <Card>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-slate-900">{t('customer.contact.title')}</h2>
            <label className="flex items-center gap-2 text-[12px] text-slate-600 whitespace-nowrap">
              <input
                type="checkbox"
                checked={contact.enabled}
                onChange={(e) => setContact({ enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
              />
              {t('customer.contact.enable')}
            </label>
          </div>

          {contact.enabled ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('customer.contact.firstName')}>
                  <input className={INPUT} value={contact.firstName} onChange={(e) => setContact({ firstName: e.target.value })} />
                </Field>
                <Field label={t('customer.contact.lastName')}>
                  <input className={INPUT} value={contact.lastName} onChange={(e) => setContact({ lastName: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label={t('customer.contact.role')}>
                  <input className={INPUT} value={contact.role} onChange={(e) => setContact({ role: e.target.value })} />
                </Field>
                <Field label={t('customer.contact.email')}>
                  <input className={INPUT} type="email" value={contact.email} onChange={(e) => setContact({ email: e.target.value })} />
                </Field>
                <Field label={t('customer.contact.phone')}>
                  <input className={INPUT} value={contact.phone} onChange={(e) => setContact({ phone: e.target.value })} />
                </Field>
              </div>
            </div>
          ) : (
            <p className="text-[12px] text-slate-400">{t('customer.contact.disabledHint')}</p>
          )}
        </div>
      </Card>

      <ContractStepFooter back="upload" next="object" />
    </div>
  );
}

export default CustomerStep;
